import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import clsx from "clsx";
import { Typography } from "@mui/material";

import makeStyles from "@mui/styles/makeStyles";

import { palette } from "../../../theme/palette";

import { validateRoute } from "../../../utils/routes";

import { selectedRouteSelector } from "../../../reducers/ownApp/selectors";
import {
  maxAdditionalMinutesSelector,
  routeConfigSelector,
  routeEditSelector,
} from "../../../reducers/routeManagement/selectors";
import {
  updateRouteEdit,
  setIsRouteEditValid,
  updateRouteEditRoadSpeed,
} from "../../../reducers/routeManagement/thunkActions";

import { RouteManagementModalContent } from "./RouteManagementModalContent";
import { View } from "../../View";

const useStyles = makeStyles({
  container: {
    flex: 1,
    margin: "0.5%",
    padding: "0 0 0.25% 0",
    maxWidth: "31.5rem",
    maxHeight: "99%",
    backgroundColor: palette.white,
    borderRadius: "0.4rem",
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    zIndex: 101,
  },
  hidden: {
    display: "none",
  },
  top: {
    borderRadius: "0.4rem 0.4rem 0rem 0rem",
    backgroundColor: palette.dpdLightGrey,
  },
  title: {
    padding: "1rem 1.6rem",
  },
  bottom: {
    display: "flex",
    marginTop: "0.5rem",
  },
});

export const RouteManagementModal = ({ hidden }) => {
  const styles = useStyles();
  const dispatch = useDispatch();
  const maxAdditionalMinutes = useSelector(maxAdditionalMinutesSelector);
  const routeConfig = useSelector(routeConfigSelector);
  const routeEdit = useSelector(routeEditSelector);
  const selectedRoute = useSelector(selectedRouteSelector);
  const selectedRouteCode = (routeEdit || selectedRoute)?.routeCode;
  const hasSelectedRoute = !!selectedRouteCode;
  const [isProcessing, setIsProcessing] = useState(false);

  const errors = validateRoute({
    route: routeEdit || selectedRoute,
    maxAdditionalMinutes,
    routeConfig,
  });
  const isValid = !Object.keys(errors).length;

  useEffect(() => {
    dispatch(setIsRouteEditValid(isValid));
  }, [isValid, dispatch]);

  const onChange = useCallback(
    async ({ target: { name, value } }) => {
      if (isProcessing) return;
      setIsProcessing(true);
      await dispatch(updateRouteEdit({ name, value }));
      setIsProcessing(false);
    },
    [dispatch, isProcessing]
  );

  const onRoadSpeedChange = useCallback(
    async ({ target: { roadCode, name, value } }) => {
      if (isProcessing) return;
      setIsProcessing(true);
      await dispatch(updateRouteEditRoadSpeed({ roadCode, name, value }));
      setIsProcessing(false);
    },
    [dispatch, isProcessing]
  );

  if (!hasSelectedRoute) return null;
  else
    return (
      <View className={clsx(styles.container, hidden && styles.hidden)}>
        <View className={styles.top}>
          <Typography className={styles.title}>
            {!routeEdit ? "ROUTE DETAILS" : "EDIT ROUTE"}
          </Typography>
        </View>
        <View className={styles.bottom}>
          <RouteManagementModalContent
            routeConfig={routeConfig}
            route={routeEdit || selectedRoute}
            onChange={onChange}
            onRoadSpeedChange={onRoadSpeedChange}
            disabled={!routeEdit}
            errors={errors}
          />
        </View>
      </View>
    );
};

export default RouteManagementModal;
